import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BuildIcon from '@mui/icons-material/Build';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { isServiceTitanPortalV2Company } from '_lib/api/apiTypes.shim';
import { Box } from '_lib/components/Box/Box';
import { Button } from '_lib/components/Button/Button';
import { MaskedPhoneDisplay } from '_lib/components/MaskedPhoneDisplay/MaskedPhoneDisplay';
import { Stack } from '_lib/components/Stack/Stack';
import { TabPanel } from '_lib/components/TabPanel/TabPanel';

import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { useFixStPortalDialog } from '_lib/hooks/FixStPortalDialogHook';
import { PrimaryHeader, StyledDivider, StyledTab, StyledTabs, ContentBox } from '_lib/styles/PhoneCall.styled';
import { PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { isNonEmptyDnisArray } from '_lib/utils/helpers';

import { logDebug } from '_lib/utils/logging';
import { getCallPopDnis } from '_lib/utils/urlStateUtils';

import { AiCall } from '../AiCall/AiCall';
import { CompanyContext, ZoneZipContext } from '../Company.slice';
import { CompanyAllZones } from '../CompanyAllZones/CompanyAllZones';
import { CompanyClock } from '../CompanyClock/CompanyClock';
import { CompanyContact } from '../CompanyContact/CompanyContact';
import { CompanyDetails } from '../CompanyDetails/CompanyDetails';
import { CompanyNotes } from '../CompanyNotes/CompanyNotes';
import { CompanyPaneProps } from '../CompanyPane';
import { CompanySummary } from '../CompanySummary/CompanySummary';

import { DefaultCompanyZipZoneSelect } from './DefaultCompanyZipZoneSelect';
import { ZipZoneSelectContext } from './zipZoneSelectContext';

interface CompanySelectedProps {
  onCompanySwitchConfirmed: (companyId: string) => void;
  companyContext: CompanyContext;
  zoneZipContext: ZoneZipContext | undefined;
  phoneCallURLState: Pick<PhoneCallURLState, 'companyDNIS'>;
  hideAllTabs?: boolean; // This is required because nested tabs are not 'rendered', causing an error
  iframeContext?: CompanyPaneProps['iframeContext'];
  aiCall?: CompanyPaneProps['aiCall'];
}

export const CompanySelected = (props: CompanySelectedProps): React.ReactElement => {
  const {
    onCompanySwitchConfirmed,
    companyContext,
    zoneZipContext,
    phoneCallURLState,
    hideAllTabs,
    iframeContext,
    aiCall
  } = props;
  const { company } = companyContext;

  const [tabIndex, setTabIndex] = useState(aiCall ? 0 : 1);

  /* Injected Zip/Zone selection component, should be 'Provided' higher up the component tree */
  const ProviderCompanyZipZoneSelect = useContext(ZipZoneSelectContext);

  const currentCompanyDNIS =
    companyContext.switched && isNonEmptyDnisArray(company.callCenter.dnisArray)
      ? getCallPopDnis(company.callCenter.dnisArray, phoneCallURLState.companyDNIS)
      : phoneCallURLState.companyDNIS;

  const showSTPortalButton = !!(
    isServiceTitanPortalV2Company(company) &&
    !company.dataProvider.providerOptions.isEnterprise &&
    iframeContext?.callHandleIframeUrl
  );
  const showSTEnterpriseButton = !!(
    isServiceTitanPortalV2Company(company) &&
    company.dataProvider.providerOptions.isEnterprise &&
    iframeContext?.callHandleIframeUrl
  );

  const [fixStPortalDialogIsOpen, setFixStPortalDialogIsOpen] = useState(false);
  const fixStPortalDialogHook = useFixStPortalDialog();

  const [scheduleUrlAnimationEnabled, setScheduleUrlAnimationEnabled] = useState<boolean>(false);
  const scheduleUrlAnimationPreviouslyTriggered = useRef<boolean>(false);

  useEffect(() => {
    const canShowScheduleUrlAnimation =
      isServiceTitanPortalV2Company(company) &&
      company.dataProvider.providerOptions.useAdaptiveCapacity &&
      !!(
        (company.isZoned && zoneZipContext?.selectedZone && !zoneZipContext.selectedZone.contactScheduleUrl) ||
        (!company.isZoned &&
          !company.callCenter.scheduleUrl &&
          ((zoneZipContext?.selectedZipOrPostal &&
            (zoneZipContext.overrideZipMatch === true || zoneZipContext.overrideZipMatch === null)) ||
            zoneZipContext?.overrideZipMatch === undefined))
      );

    if (!scheduleUrlAnimationPreviouslyTriggered.current && canShowScheduleUrlAnimation) {
      scheduleUrlAnimationPreviouslyTriggered.current = true;
      setScheduleUrlAnimationEnabled(true);
      logDebug('CompanySelected', 'Schedule URL animation triggered');
    } else {
      setScheduleUrlAnimationEnabled(false);
      logDebug('CompanySelected', 'Schedule URL animation reset');
    }
  }, [company, zoneZipContext?.overrideZipMatch, zoneZipContext?.selectedZipOrPostal, zoneZipContext?.selectedZone]);

  return (
    <>
      <PrimaryHeader>
        <Stack useFlexGap direction='row' sx={{ justifyContent: 'space-between', paddingX: 1.5, paddingY: 1 }}>
          <Stack useFlexGap direction='column' spacing={0}>
            <Typography variant='h3' text={company.name} />
            <MaskedPhoneDisplay variant='h3' text={currentCompanyDNIS || ''} />
          </Stack>
          <Stack useFlexGap direction='column' spacing={0} sx={{ minWidth: '25%' }}>
            <CompanyClock
              variant='h4'
              timezone={company.timezone}
              textAlign={showSTPortalButton || showSTEnterpriseButton ? 'center' : 'end'}
            />
            {showSTPortalButton && (
              <Tooltip title='Go to ServiceTitan Call Handle' placement='bottom'>
                <Button
                  color='inherit'
                  size='small'
                  sx={{
                    alignSelf: 'center',
                    whiteSpace: 'nowrap',
                    paddingX: 1,
                    paddingY: 0.5
                  }}
                  startIcon={<OpenInNewIcon />}
                  onClick={async () => window.open(iframeContext.callHandleIframeUrl, '_blank')}>
                  OPEN ST
                </Button>
              </Tooltip>
            )}
            {showSTEnterpriseButton && (
              <Tooltip title='Login and Call Handle redirects, or open Call Handle in new tab' placement='bottom'>
                <Button
                  color='inherit'
                  size='small'
                  disabled={fixStPortalDialogIsOpen}
                  sx={{
                    alignSelf: 'center',
                    whiteSpace: 'nowrap',
                    paddingX: 1,
                    paddingY: 0.5
                  }}
                  startIcon={<BuildIcon />}
                  onClick={async () => {
                    setFixStPortalDialogIsOpen(true);
                    await fixStPortalDialogHook.show({
                      iframeContext,
                      container: iframeContext.iframePaneRef.current,
                      disableEnforceFocus: true
                    });
                    setFixStPortalDialogIsOpen(false);
                  }}>
                  FIX ST
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        <Box sx={{ px: 2, py: 1 }}>
          {ProviderCompanyZipZoneSelect ? (
            <ProviderCompanyZipZoneSelect
              onCompanySwitchConfirmed={onCompanySwitchConfirmed}
              companyContext={companyContext}
              zoneZipContext={zoneZipContext}
            />
          ) : (
            /* Our 'default' companyZoneZipSelect that can be overwritten via ProviderCompanyZipZoneSelect */
            <DefaultCompanyZipZoneSelect
              onCompanySwitchConfirmed={onCompanySwitchConfirmed}
              companyContext={companyContext}
              zoneZipContext={zoneZipContext}
            />
          )}
        </Box>
        <StyledDivider />
        {!hideAllTabs && (
          <StyledTabs
            value={tabIndex}
            centered={false}
            variant='scrollable'
            scrollButtons='auto'
            onChange={(_event: React.SyntheticEvent, newValue: number) => {
              setTabIndex(newValue);
              if (scheduleUrlAnimationEnabled) {
                setScheduleUrlAnimationEnabled(false);
                logDebug('CompanySelected', 'Schedule URL animation disabled from tab change');
              }
            }}>
            {!!aiCall && (
              <StyledTab
                icon={<AutoAwesomeIcon />}
                iconPosition='start'
                label={'CPI SUMMARY'}
                id='company-tab-0'
                value={0}
                sx={(theme) => ({
                  minHeight: '0px',
                  position: 'relative',
                  overflow: 'hidden',
                  zIndex: 0,
                  '&::before': {
                    content: '""',
                    zIndex: -2,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(0deg)',
                    width: '999px',
                    height: '999px',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: '0 0',
                    backgroundImage: `conic-gradient(
                    rgba(52, 168, 82, 0) 0deg,
                    rgba(52, 168, 82, 1) 6deg,
                    rgba(255, 211, 20, 1) 18deg,
                    rgba(255, 70, 65, 1) 26deg,
                    rgba(49, 134, 255, 1) 36deg,
                    rgba(49, 134, 255, 1) 44deg,
                    rgba(49, 134, 255, 0) 56deg
                  )`,
                    animation: 'rotateBorderFadeOut 10s linear 1 forwards',
                    filter: 'blur(5px)'
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    zIndex: -1,
                    left: '6px',
                    top: '6px',
                    width: 'calc(100% - 12px)',
                    height: 'calc(100% - 12px)',
                    background: theme.palette.primary.main,
                    borderRadius: '4px'
                  },
                  '@keyframes rotateBorderFadeOut': {
                    '0%': {
                      transform: 'translate(-50%, -50%) rotate(0deg)',
                      opacity: 1
                    },
                    '90%': {
                      transform: 'translate(-50%, -50%) rotate(4turn)',
                      opacity: 1
                    },
                    '100%': {
                      transform: 'translate(-50%, -50%) rotate(4turn)',
                      opacity: 0
                    }
                  }
                })}
              />
            )}
            <StyledTab label={'SUMMARY'} id='company-tab-1' value={1} />
            <StyledTab label={'DETAILS'} id='company-tab-2' value={2} />
            <StyledTab label={'CONTACT'} id='company-tab-3' value={3} />
            <StyledTab label={'NOTES'} id='company-tab-4' value={4} />
            {company.isZoned && <StyledTab label={'ALL ZONES'} id='company-tab-5' value={5} />}
          </StyledTabs>
        )}
      </PrimaryHeader>

      {!!aiCall && (
        <TabPanel currentIndex={tabIndex} index={0}>
          <ContentBox>
            <AiCall aiCall={aiCall} company={company} />
          </ContentBox>
        </TabPanel>
      )}
      <TabPanel currentIndex={tabIndex} index={1}>
        <ContentBox>
          <CompanySummary
            onCompanySwitchConfirmed={onCompanySwitchConfirmed}
            companyContext={companyContext}
            zoneZipContext={zoneZipContext}
            phoneCallURLState={phoneCallURLState}
            showScheduleUrlAnimation={scheduleUrlAnimationEnabled}
            disableGreeting={!!aiCall}
          />
        </ContentBox>
      </TabPanel>
      <TabPanel currentIndex={tabIndex} index={2}>
        <ContentBox>
          <CompanyDetails companyContext={companyContext} zoneZipContext={zoneZipContext} />
        </ContentBox>
      </TabPanel>
      <TabPanel currentIndex={tabIndex} index={3}>
        <ContentBox>
          <CompanyContact companyContext={companyContext} zoneZipContext={zoneZipContext} />
        </ContentBox>
      </TabPanel>
      <TabPanel currentIndex={tabIndex} index={4}>
        <ContentBox>
          <CompanyNotes companyContext={companyContext} zoneZipContext={zoneZipContext} />
        </ContentBox>
      </TabPanel>
      <TabPanel currentIndex={tabIndex} index={5}>
        <ContentBox>
          <CompanyAllZones company={company} />
        </ContentBox>
      </TabPanel>
      {fixStPortalDialogHook.element}
    </>
  );
};
